import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import UserUtils from "../../../basecomp/auth/UserUtils";
import sanitizeText from "../../../basecomp/base/TextSanitizer";
import {
  addReceive,
  updateReceive,
  fetchMaterialBySq01,
  fetchLatestReceiveByPo,
} from "../model/IssueReceiveModel";

const { getUserName } = UserUtils;

const emptyRow = () => ({
  material_no: "",
  material_desc: "",
  qty: "",
  uom: "",
  storage_loc: "",
  storage_bin: "",
  remarks: "",
  // True once Material Description/UOM have been auto-filled from sq01 —
  // locks those two fields so they can't be hand-edited after a real lookup.
  material_locked: false,
  // True once Storage Location/Bin Location resolved to a single confirmed
  // pair (either only one match existed, or the user picked one from the
  // dropdown) — locks both fields.
  location_locked: false,
  // Populated with more than one {storage_loc, bin_location} pair when the
  // material has multiple locations — the Storage Location field renders as
  // a dropdown of these instead of a plain text input.
  location_options: [],
});

const INITIAL_ROWS = 2;

const isRowFilled = (row) => !!(row.material_no && row.qty);

// A barcode scanner emulates a keyboard but emits characters within
// ~5-20ms of each other — far faster than any human can sustain while
// hand-typing. One inter-keystroke gap above this threshold means the
// current Material Number is being typed, not scanned.
const SCAN_MAX_GAP_MS = 50;
// Debounce before auto-looking-up the Material Number once typing/scanning
// stops, so the row auto-advances without needing Enter. A scan finishes
// essentially instantly, so a short pause safely means "done". Hand-typing
// gets a longer one — long enough that a mid-number pause (re-checking a
// slip, hunt-and-peck typing) doesn't get mistaken for "finished" and
// validated as incomplete/wrong, which is what a too-short delay caused
// before. Enter and leaving the field (onBlur, in ReceiveView.jsx) still
// trigger the same lookup instantly, without waiting for either delay.
const SCAN_DEBOUNCE_MS = 400;
const MANUAL_TYPE_DEBOUNCE_MS = 2000;

// A material number: starts with 1 or 5, 5, 6, or 8 digits total (never 7).
const MATERIAL_NO_PATTERN = /^[15](\d{4}|\d{5}|\d{7})$/;

const ReceiveVm = () => {
  const { handleToastOpen } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [po_no, setPoNo] = useState("");
  // Defaults to the logged-in operator's name rather than being typed by
  // hand, since it's meant to record who actually processed the receipt.
  // Still editable, and an existing record's saved value (below) always
  // takes precedence when editing.
  const [received_by, setReceivedBy] = useState(() => getUserName() || "");
  const [delivered_by, setDeliveredByRaw] = useState("");
  const setDeliveredBy = (value) => setDeliveredByRaw(sanitizeText(value));
  const [remarks, setRemarksRaw] = useState("");
  const setRemarks = (value) => setRemarksRaw(sanitizeText(value));
  // A drawn signature (PNG data URL), captured live at Delivered By — not
  // typed text, so it doesn't go through sanitizeText.
  const [signature, setSignature] = useState("");
  const [rows, setRows] = useState(() =>
    Array.from({ length: INITIAL_ROWS }, emptyRow)
  );
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // Set to the material number that resolved to nothing at all (no
  // description/UOM and no location data) — drives the "not found" popup.
  const [notFoundMaterialNo, setNotFoundMaterialNo] = useState(null);
  const dismissNotFound = () => setNotFoundMaterialNo(null);
  // Only true once the PO number has been resolved from the URL and any
  // existing record for it has finished loading — the form doesn't render
  // until then, so a refresh never flashes a blank/editable form.
  const [formReady, setFormReady] = useState(false);
  // Per-(row, field) keystroke timing — lets the auto-lookup effect below
  // tell a barcode scan from hand-typing (see SCAN_MAX_GAP_MS) so a slow
  // typist's mid-entry pause is never mistaken for "done typing" and
  // validated as an incomplete/incorrect number. Keyed by `${rowIndex}` for
  // Material Number (its own dedicated debounce effect below) and by
  // `${rowIndex}:${field}` for every other field (used only to size the
  // mis-scan-redirect debounce below).
  const scanTimingRef = useRef({});
  // Debounce timers for the mis-scan redirect (see MATERIAL_NO_PATTERN
  // below), keyed by `${rowIndex}:${field}`.
  const fieldRedirectTimersRef = useRef({});
  useEffect(() => {
    return () => {
      Object.values(fieldRedirectTimersRef.current).forEach(clearTimeout);
    };
  }, []);

  // The PO number lives in the URL (?po_no=...), not router state, so it
  // survives a page refresh. On every mount (including after a refresh) we
  // re-resolve it here and re-fetch any existing record ourselves, rather
  // than relying on data handed over from the landing page.
  useEffect(() => {
    const poFromUrl = (searchParams.get("po_no") || "").trim();
    if (!poFromUrl) {
      // Nothing to work on without a PO number — send the user back rather
      // than showing an empty, freely-editable form.
      navigate("/warehouse/issue-receive", { replace: true });
      return;
    }
    setPoNo(poFromUrl);
    setLoading(true);
    fetchLatestReceiveByPo(poFromUrl)
      .then((existing) => {
        const detail = existing?.data;
        if (detail) {
          setReceivedBy(detail.received_by || "");
          setDeliveredBy(detail.delivered_by || "");
          setRemarks(detail.remarks || "");
          setSignature(detail.signature || "");
          const filledRows = (detail.items || []).map((item) => ({
            material_no: item.material_no || "",
            material_desc: item.material_desc || "",
            qty: item.qty || "",
            uom: item.uom || "",
            storage_loc: item.storage_loc || "",
            storage_bin: item.storage_bin || "",
            remarks: item.remarks || "",
            material_locked: false,
            location_locked: false,
            location_options: [],
          }));
          setRows([...filledRows, emptyRow()]);
          setEditingId(detail.id);
        }
      })
      .catch((error) => {
        handleToastOpen("error", error.message);
      })
      .finally(() => {
        setLoading(false);
        setFormReady(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Finds the first (topmost) row with no Material Number yet, or appends a
  // fresh one if every row already has one (shouldn't normally happen), and
  // assigns the given value there. Rows are meant to fill top-down with no
  // gaps, so an earlier empty row (e.g. left behind by a "not found"/
  // duplicate reset) always takes priority over a later one. Shared by the
  // per-row-field redirect below and the header-field redirect (Delivered
  // By/Remarks) further down.
  const assignMaterialNoToLatestEmptyRow = (materialNo) => {
    setRows((prev) => {
      const updated = [...prev];
      let targetIndex = -1;
      for (let i = 0; i < updated.length; i += 1) {
        if (!updated[i].material_no) {
          targetIndex = i;
          break;
        }
      }
      if (targetIndex === -1) {
        updated.push(emptyRow());
        targetIndex = updated.length - 1;
      }
      updated[targetIndex] = {
        ...updated[targetIndex],
        material_no: materialNo,
        material_locked: false,
        location_locked: false,
        location_options: [],
      };
      return updated;
    });
  };

  // Moves a value shaped like a Material Number (see MATERIAL_NO_PATTERN)
  // out of a row field it landed in — a mis-click, or a scanner firing
  // before the right field had focus — and into the latest empty row's
  // Material Number field instead.
  const redirectMaterialNoScan = (sourceRowIndex, sourceField, materialNo) => {
    setRows((prev) => {
      if (!prev[sourceRowIndex]) {
        return prev;
      }
      const updated = [...prev];
      updated[sourceRowIndex] = { ...updated[sourceRowIndex], [sourceField]: "" };
      return updated;
    });
    assignMaterialNoToLatestEmptyRow(materialNo);
  };

  // Same redirect, for the header fields (Delivered By / Remarks) — not
  // part of `rows`, so there's no source field to clear inside setRows;
  // the caller's own setter already writes "" back.
  const createHeaderMaterialNoRedirectHandler = (fieldKey, rawSetter) => (value) => {
    rawSetter(value);
    const sanitizedValue = sanitizeText(value);
    const timingKey = `header:${fieldKey}`;
    const now = Date.now();
    const timing = scanTimingRef.current[timingKey];
    const isScan =
      !timing || sanitizedValue.length <= 1
        ? true
        : timing.isScan && now - timing.time <= SCAN_MAX_GAP_MS;
    scanTimingRef.current[timingKey] = { time: now, isScan };

    clearTimeout(fieldRedirectTimersRef.current[timingKey]);
    fieldRedirectTimersRef.current[timingKey] = setTimeout(() => {
      if (MATERIAL_NO_PATTERN.test(sanitizedValue)) {
        rawSetter("");
        assignMaterialNoToLatestEmptyRow(sanitizedValue);
      }
    }, isScan ? SCAN_DEBOUNCE_MS : MANUAL_TYPE_DEBOUNCE_MS);
  };

  const setDeliveredByWithRedirect = createHeaderMaterialNoRedirectHandler(
    "delivered_by",
    setDeliveredBy
  );
  const setRemarksWithRedirect = createHeaderMaterialNoRedirectHandler(
    "remarks",
    setRemarks
  );

  const updateRowField = (rowIndex, field) => (value) => {
    // Quantities are always whole numbers — strip anything but digits so a
    // decimal point (or paste) can never sneak one in. Every other field is
    // restricted to letters/numbers/space/-_/. so special characters can't
    // sneak in via keyboard or scan.
    const sanitizedValue =
      field === "qty" ? value.toString().replace(/\D/g, "") : sanitizeText(value);

    const now = Date.now();
    const timingKey = field === "material_no" ? rowIndex : `${rowIndex}:${field}`;
    const timing = scanTimingRef.current[timingKey];
    // A short/first value can't be judged yet, so default to "scan" — one
    // slow gap later is enough to flip it to manual and stick.
    const isScan =
      !timing || sanitizedValue.length <= 1
        ? true
        : timing.isScan && now - timing.time <= SCAN_MAX_GAP_MS;
    scanTimingRef.current[timingKey] = { time: now, isScan };

    if (field !== "material_no") {
      // Debounced so a manually-typed value passing through a matching
      // intermediate length isn't redirected before it's actually finished
      // (a 6- or 8-digit number's first 5 digits already match on their
      // own) — same reasoning as the Material Number auto-lookup above.
      clearTimeout(fieldRedirectTimersRef.current[timingKey]);
      fieldRedirectTimersRef.current[timingKey] = setTimeout(() => {
        if (MATERIAL_NO_PATTERN.test(sanitizedValue)) {
          redirectMaterialNoScan(rowIndex, field, sanitizedValue);
        }
      }, isScan ? SCAN_DEBOUNCE_MS : MANUAL_TYPE_DEBOUNCE_MS);
    }

    setRows((prev) => {
      const updated = [...prev];
      updated[rowIndex] = {
        ...updated[rowIndex],
        [field]: sanitizedValue,
        // Editing the material number invalidates any previous lookup.
        ...(field === "material_no"
          ? { material_locked: false, location_locked: false, location_options: [] }
          : {}),
      };
      // Auto-grow: once the last visible row is filled, append a fresh one.
      if (rowIndex === updated.length - 1 && isRowFilled(updated[rowIndex])) {
        updated.push(emptyRow());
      }
      return updated;
    });
  };

  // Looks up the material (description/UOM from sq01, Storage/Bin Location
  // options from sbin_location scoped to this plant) and auto-fills what it
  // finds:
  //  - Re-scanning a material number that's already on another row bumps
  //    that row's Qty by one instead of creating a duplicate line — this
  //    row (which was just being filled in) is cleared back to blank,
  //    ready for the next scan.
  //  - Otherwise, Description/UOM lock once found, and Qty defaults to 1
  //    (only if not already typed) so a bare scan is immediately a valid line.
  //  - A single Storage/Bin Location pair auto-fills + locks both fields.
  //  - Multiple pairs are left for the user to pick via a dropdown (Storage
  //    Location field renders as a Select when row.location_options.length > 1).
  //  - If NOTHING at all is found, the row's material fields are cleared and
  //    the "not found" popup is shown.
  // Returns "duplicate" | "not_found" | true (description/UOM locked) |
  // false, so the caller can decide where to move focus (a disabled field
  // can never receive focus()).
  const lookupMaterial = async (rowIndex) => {
    // Read the live DOM value rather than trusting `rows` here: a fast scan
    // fires several onChange events (each queuing its own setRows) back to
    // back, and if this runs before React has committed the last of them,
    // `rows` in this closure is still one keystroke behind — truncating a
    // scanned "117" to "11". The actual <input> element's value is always
    // current, since the browser applies keystrokes to it synchronously.
    const inputEl = document.getElementById(`material_no_input_${rowIndex}`);
    const material_no = inputEl
      ? sanitizeText(inputEl.value)
      : rows[rowIndex]?.material_no;
    if (!material_no) {
      return false;
    }

    const duplicateIndex = rows.findIndex(
      (row, idx) => idx !== rowIndex && row.material_no === material_no
    );
    if (duplicateIndex !== -1) {
      setRows((prev) => {
        const updated = [...prev];
        if (!updated[duplicateIndex] || !updated[rowIndex]) {
          return prev;
        }
        const existingQty = Number(updated[duplicateIndex].qty) || 0;
        updated[duplicateIndex] = {
          ...updated[duplicateIndex],
          qty: String(existingQty + 1),
        };
        updated[rowIndex] = emptyRow();
        return updated;
      });
      return "duplicate";
    }

    try {
      const response = await fetchMaterialBySq01(material_no);
      if (!response) {
        setRows((prev) => {
          const updated = [...prev];
          if (!updated[rowIndex]) {
            return prev;
          }
          updated[rowIndex] = {
            ...updated[rowIndex],
            material_no: "",
            material_desc: "",
            uom: "",
            storage_loc: "",
            storage_bin: "",
            material_locked: false,
            location_locked: false,
            location_options: [],
          };
          return updated;
        });
        setNotFoundMaterialNo(material_no);
        return "not_found";
      }

      const locations = response.data.locations || [];
      const singleLocation = locations.length === 1 ? locations[0] : null;

      setRows((prev) => {
        const updated = [...prev];
        if (!updated[rowIndex]) {
          return prev;
        }
        updated[rowIndex] = {
          ...updated[rowIndex],
          // Reconcile against whatever the lookup actually resolved, in
          // case state was still a keystroke behind the live DOM value.
          material_no,
          material_desc: response.data.material_desc || "",
          uom: response.data.uom || "",
          material_locked: true,
          qty: updated[rowIndex].qty || "1",
          ...(singleLocation
            ? {
                storage_loc: singleLocation.storage_loc,
                storage_bin: singleLocation.bin_location,
                location_locked: true,
                location_options: [],
              }
            : { location_locked: false, location_options: locations }),
        };
        // Auto-grow: Qty just got auto-filled, so this row is now "filled"
        // without the user ever touching the Qty field — replicate the same
        // auto-grow check updateRowField normally does.
        if (
          rowIndex === updated.length - 1 &&
          isRowFilled(updated[rowIndex])
        ) {
          updated.push(emptyRow());
        }
        return updated;
      });
      return true;
    } catch (error) {
      // Lookup failure — leave the fields editable.
      return false;
    }
  };

  // Picks one Storage/Bin Location pair from the dropdown, locking both
  // fields to that combination.
  const selectLocation = (rowIndex, location) => {
    setRows((prev) => {
      const updated = [...prev];
      if (!updated[rowIndex]) {
        return prev;
      }
      updated[rowIndex] = {
        ...updated[rowIndex],
        storage_loc: location.storage_loc,
        storage_bin: location.bin_location,
        location_locked: true,
      };
      return updated;
    });
  };

  // Auto-fetch as soon as the user stops typing/scanning a Material Number
  // — no Enter required, the row auto-advances on its own. Keyed off each
  // row's material_no + lock state (not the whole `rows` array) so editing
  // an unrelated field doesn't reset the debounce, and a row already locked
  // doesn't keep re-firing.
  const materialNoKey = rows
    .map((row) => `${row.material_no}:${row.material_locked}`)
    .join(",");

  useEffect(() => {
    const timers = rows.map((row, idx) => {
      if (row.material_no && !row.material_locked) {
        const isScan = scanTimingRef.current[idx]?.isScan !== false;
        const delay = isScan ? SCAN_DEBOUNCE_MS : MANUAL_TYPE_DEBOUNCE_MS;
        return setTimeout(async () => {
          const result = await lookupMaterial(idx);
          // Mirrors the Enter-key focus routing below — a re-scan/not-found
          // stays put for another attempt, a successful lookup jumps ahead
          // to the next row so scanning can continue without ever touching
          // Enter or tapping the screen.
          const focusId =
            result === "not_found" || result === "duplicate"
              ? `material_no_input_${idx}`
              : result === true
              ? `material_no_input_${idx + 1}`
              : null;
          if (focusId) {
            // Deferred: a newly auto-grown row is pushed inside the same
            // setRows call as this lookup, so it isn't in the DOM yet when
            // this callback runs — focusing now would silently no-op.
            setTimeout(() => {
              const el = document.getElementById(focusId);
              if (el) {
                el.focus();
              }
            }, 0);
          }
        }, delay);
      }
      return null;
    });
    return () => {
      timers.forEach((timer) => timer && clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialNoKey]);

  const addMoreRows = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const removeRow = (rowIndex) => {
    setRows((prev) => prev.filter((_, i) => i !== rowIndex));
  };

  const resetForm = () => {
    setPoNo("");
    setReceivedBy(getUserName() || "");
    setDeliveredBy("");
    setRemarks("");
    setSignature("");
    setRows(Array.from({ length: INITIAL_ROWS }, emptyRow));
    setEditingId(null);
  };

  const getFilledRows = () =>
    rows.filter((row) => row.material_no && row.qty);

  const handleSave = async () => {
    if (!po_no) {
      handleToastOpen("error", "PO number is required.");
      return;
    }
    const items = getFilledRows();
    if (items.length === 0) {
      handleToastOpen("error", "Please fill at least one material row.");
      return;
    }
    setLoading(true);
    try {
      const payload = { po_no, received_by, delivered_by, remarks, items, signature };
      if (editingId) {
        await updateReceive(editingId, payload);
        handleToastOpen("success", "Receive updated successfully");
      } else {
        await addReceive(payload);
        handleToastOpen("success", "Material received successfully");
      }
      resetForm();
      navigate("/warehouse/issue-receive");
    } catch (error) {
      handleToastOpen("error", error.message);
    } finally {
      setLoading(false);
    }
  };

  return {
    po_no,
    setPoNo,
    received_by,
    setReceivedBy,
    delivered_by,
    setDeliveredBy: setDeliveredByWithRedirect,
    remarks,
    setRemarks: setRemarksWithRedirect,
    signature,
    setSignature,
    rows,
    updateRowField,
    lookupMaterial,
    selectLocation,
    notFoundMaterialNo,
    dismissNotFound,
    addMoreRows,
    removeRow,
    handleSave,
    loading,
    editingId,
    resetForm,
    formReady,
  };
};

export default ReceiveVm;
